import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { useId, useState, type ReactNode } from "react";
import { Input, Label, TextField } from "react-aria-components";
import { useGestaltung } from "../design/gestaltung";
import { Abweisung } from "../design/Hinweis";
import { Kachel } from "../design/Kachel";
import { useEingabesperre, useSperre } from "../design/sperre";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useDokument } from "../plattform/lesemodelle";
import { useSitzung } from "../plattform/sitzung";
import type { Zustandsanzeige } from "./jagdzustand";
import { useKopf } from "./teile";
import { Zustandsband } from "./Zustandsband";

/**
 * Auslöser der Leitung (oberflaeche.md §4.3, §5, §9; design/README §2.3, §9.1, §9.5; ADR-0023): während die Jagd im
 * Gange ist „Hahn in Ruh“ (Jagdleiter, Jagdherr), während sie ruht „Jagd freigeben“ (Jagdherr). Neutral mit
 * Handlungssymbol (Hand bzw. Schloss), nie das Zustandssymbol. Beide wirken nur nach der Bestätigung im
 * `alertdialog` mit dem Namen der Handlung, ohne Rückgängig; „Abbrechen“ sendet nichts (N-05). Der Dialog steht an
 * Stelle des Inhalts der Ansicht, in denselben Zeilen (Referenz `#s-dialog-ruhe`): oben Kopf und Text („Sofort für alle
 * Geräte · kein Rückgängig“), „Bestätigen“ in Farbe und Symbol des Zielzustands über den Abdrücken beider Auslöser,
 * darunter „Grund“ (optional, K29) und „Abbrechen“. 500 ms Eingabesperre.
 *
 * Ob die Person die Rolle hat, entscheidet der Server; das Gerät zeigt den Auslöser nach den Rollen aus „Meine
 * Drückjagden“ (lesemodelle.md §6).
 */
type Handlung = "Hahn in Ruh" | "Jagd freigeben";

export interface Ausloeser {
  /** Kachel des Auslösers (oder nichts, wenn die Person keinen hat) samt einer Abweisung */
  kachel: ReactNode;
  /** geöffneter Bestätigungsdialog als ganze Ansicht; die gastgebende Ansicht zeigt ihn an ihrer Stelle */
  dialog: ((gastgeber: { name: string }) => ReactNode) | null;
}

export function useAusloeser(drueckjagdId: string, anzeige: Zustandsanzeige): Ausloeser {
  const { uid } = useSitzung();
  const eintrag = useDokument(uid ? `personen/${uid}/meine_drueckjagden/${drueckjagdId}` : null);
  const rollen = (eintrag.daten?.["Rollen"] as string[] | undefined) ?? [];
  const jagdherr = rollen.includes("Jagdherr");
  const leitung = jagdherr || rollen.includes("Jagdleiter");
  const [offen, setOffen] = useState<Handlung | null>(null);
  const [abgewiesen, setAbgewiesen] = useState<string | null>(null);

  // Solange der Jagdzustand nicht bekannt ist, steht kein Auslöser da: er könnte der falsche sein (§4.1, N-05).
  const begriff: Handlung | null = !anzeige.bekannt ? null
    : anzeige.zustand === "Jagd im Gange" ? (leitung ? "Hahn in Ruh" : null) : (jagdherr ? "Jagd freigeben" : null);

  const bestaetigen = async (handlung: Handlung, grund: string) => {
    const daten: Record<string, unknown> = { Drückjagd: drueckjagdId, bestätigt: true };
    if (grund.trim()) daten["Grund"] = grund.trim();
    setOffen(null);
    const a = await befehl(handlung === "Hahn in Ruh" ? "HahnInRuhAusrufen" : "JagdFreigeben", daten);
    setAbgewiesen(a.netzfehler ? null : abweisungsgrund(a));
  };

  const kachel = begriff ? (
    <>
      <Kachel symbol={begriff === "Hahn in Ruh" ? "ausrufen" : "freigeben"} begriff={begriff} art="ausloeser"
        onPress={() => { setAbgewiesen(null); setOffen(begriff); }} />
      <Abweisung grund={abgewiesen} />
    </>
  ) : <Abweisung grund={abgewiesen} />;

  return {
    kachel,
    dialog: offen ? ({ name }) => (
      <Bestaetigungsdialog drueckjagdId={drueckjagdId} anzeige={anzeige} handlung={offen} name={name}
        onBestaetigen={(grund) => void bestaetigen(offen, grund)} onAbbrechen={() => setOffen(null)} />
    ) : null,
  };
}

/**
 * Rahmen eines Bestätigungsdialogs an Stelle einer Ansicht der Drückjagd (design/README §9.5): Zustandsband mit Titel
 * ohne „Zurück“, `main` in den Zeilen des Lagebilds, 500 ms Eingabesperre beim Öffnen (`schluessel` wechselt je Dialog).
 */
export function DialogRahmen({ drueckjagdId, anzeige, name, titel, schluessel, children }: {
  drueckjagdId: string; anzeige: Zustandsanzeige; name: string; titel: string; schluessel: string; children: ReactNode;
}) {
  const gesperrt = useSperre("kritisch", schluessel);
  const beschaeftigt = useEingabesperre(gesperrt) || undefined;
  const wurzel = useGestaltung();
  return (
    <div ref={wurzel} className={`ansicht${gesperrt ? " ist-gesperrt" : ""}`} data-zustand={anzeige.flaeche}>
      <Zustandsband drueckjagdId={drueckjagdId} anzeige={anzeige} art="dialog" titel={{ titel }} />
      <main className="feld feld--leitung" aria-label={name} inert={gesperrt} aria-busy={beschaeftigt}>{children}</main>
    </div>
  );
}

function Bestaetigungsdialog({ drueckjagdId, anzeige, handlung, name, onBestaetigen, onAbbrechen }: {
  drueckjagdId: string; anzeige: Zustandsanzeige; handlung: Handlung; name: string;
  onBestaetigen: (grund: string) => void; onAbbrechen: () => void;
}) {
  const id = useId();
  const [grund, setGrund] = useState("");
  const kopf = useKopf(drueckjagdId);
  const ziel = handlung === "Hahn in Ruh" ? { flaeche: "ruhe", symbol: "hahn-in-ruh" } : { flaeche: "gang", symbol: "jagd-im-gange" };
  const bezeichnung = (kopf.daten?.["Bezeichnung"] as string | undefined) ?? null;
  return (
    <DialogRahmen drueckjagdId={drueckjagdId} anzeige={anzeige} name={name} titel={name} schluessel={handlung}>
        <div className="dialog" role="alertdialog" aria-labelledby={`${id}-kopf`} aria-describedby={`${id}-text`}>
          <b className="dialog__kopf platte" data-zeile-fest="" id={`${id}-kopf`}>{handlung}</b>
          <div className="dialog__text platte">
            <span id={`${id}-text`}>Sofort für alle Geräte · kein Rückgängig</span>
            <span>{[bezeichnung, berlinUhrzeit(anzeige.jetzt)].filter(Boolean).join(" · ")}</span>
          </div>
          <Kachel symbol={ziel.symbol} begriff="Bestätigen" art="kern" className="k--zustand" onPress={() => onBestaetigen(grund)} zustand={ziel.flaeche} />
          <TextField className="feld-text" value={grund} onChange={setGrund}>
            <Label className="feld-text__b">Grund</Label>
            <Input />
          </TextField>
          <Kachel symbol="schliessen" begriff="Abbrechen" art="kompakt" className="dialog__abbrechen" onPress={onAbbrechen} />
        </div>
    </DialogRahmen>
  );
}

